"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
const relatedLinks = [{
  label: "Terms",
  to: "/terms"
}, {
  label: "Contact",
  to: "/contact"
}, {
  label: "About Us",
  to: "/about-us"
}];
const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const contactMethodOptions = ["Phone", "Text", "Email"];
const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function RelatedLinksEstimateForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.consent) {
      setStatus("error");
      setErrorMessage("Please accept the contact consent to submit your request.");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
    const params = new URLSearchParams();
    params.append("name", formData.name);
    params.append("phone", formData.phone);
    params.append("email", formData.email);
    params.append("address", formData.address);
    params.append("project_type", formData.project_type);
    params.append("details", formData.details);
    params.append("contact_method", formData.contact_method);
    params.append("timing", formData.timing);
    params.append("budget", formData.budget);
    params.append("consent", formData.consent ? "true" : "false");
    params.append("website", "DecaturHomeRenovation.com");
    params.append("page", currentPath);
    params.append("lead_source", "organic");
    try {
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    }
  };
  return <section id="relatedlinksestimateform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Related Links Block */}
        <div className="mb-14 pb-12 border-b border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {relatedLinks.map((item, index) => <div key={item.label} data-index={index}>
                <Link to={item.to} className="group flex items-center justify-between p-4 rounded-xl border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-colors shadow-2xs">
                  <span className="font-serif text-lg font-semibold text-foreground tracking-tight group-hover:text-primary transition-colors">
                    {item.label}
                  </span>
                  <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </Link>
              </div>)}
          </div>
        </div>

        {/* Estimate Form Container */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border/80 bg-card/90 backdrop-blur-sm shadow-md rounded-xl">
            <CardHeader className="text-center pb-8 border-b border-border">
              <CardTitle className="font-serif text-2xl sm:text-3xl lg:text-4xl text-foreground font-semibold">
                Request a renovation estimate
              </CardTitle>
              <CardDescription className="font-sans text-sm sm:text-base text-muted-foreground mt-2 max-w-lg mx-auto">
                Tell us about your home remodel, and our team will get in touch to discuss plans, budget, and timing.
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-8">
              {status === "success" ? <div className="py-12 text-center flex flex-col items-center justify-center space-y-4">
                  <div className="p-3 bg-secondary rounded-lg text-foreground">
                    <CheckCircle2 className="size-10 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-foreground">
                    Estimate request received
                  </h3>
                  <p className="font-sans text-muted-foreground max-w-md">
                    Thank you for reaching out. We have received your project details and will contact you promptly.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6 font-sans">
                  {status === "error" && errorMessage && <Alert variant="destructive" className="mb-6">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Submission Error</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="name" type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} placeholder="e.g. Eleanor Vance" className="bg-background" />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="phone" type="tel" required value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} placeholder="Your phone number" className="bg-background" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="email" type="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="Your email address" className="bg-background" />
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-foreground">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="address" type="text" required value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} placeholder="Decatur, GA 30030" className="bg-background" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Project Type */}
                    <div className="space-y-2">
                      <Label htmlFor="project_type" className="text-foreground">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.project_type} onValueChange={val => setFormData({
                    ...formData,
                    project_type: val
                  })}>
                        <SelectTrigger id="project_type" className="w-full bg-background">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypeOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preferred Contact Method */}
                    <div className="space-y-2">
                      <Label htmlFor="contact_method" className="text-foreground">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.contact_method} onValueChange={val => setFormData({
                    ...formData,
                    contact_method: val
                  })}>
                        <SelectTrigger id="contact_method" className="w-full bg-background">
                          <SelectValue placeholder="Select contact method" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethodOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Project Timing */}
                    <div className="space-y-2">
                      <Label htmlFor="timing" className="text-foreground">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.timing} onValueChange={val => setFormData({
                    ...formData,
                    timing: val
                  })}>
                        <SelectTrigger id="timing" className="w-full bg-background">
                          <SelectValue placeholder="Select project timing" />
                        </SelectTrigger>
                        <SelectContent>
                          {timingOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Estimated Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="budget" className="text-foreground">
                        Estimated Budget
                      </Label>
                      <Select value={formData.budget} onValueChange={val => setFormData({
                    ...formData,
                    budget: val
                  })}>
                        <SelectTrigger id="budget" className="w-full bg-background">
                          <SelectValue placeholder="Select estimated budget" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-2">
                    <Label htmlFor="details" className="text-foreground">
                      Project Details
                    </Label>
                    <Textarea id="details" value={formData.details} onChange={e => setFormData({
                  ...formData,
                  details: e.target.value
                })} placeholder="Share rooms involved, architectural style, desired layout changes, or specific questions..." className="min-h-28 bg-background" />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => setFormData({
                  ...formData,
                  consent: checked === true
                })} className="mt-0.5" />
                    <Label htmlFor="consent" className="text-xs sm:text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-3">
                    <Button type="submit" disabled={status === "loading"} className="w-full h-11 text-base font-medium transition-opacity">
                      {status === "loading" ? <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Submitting Estimate Request...
                        </> : "Submit Estimate Request"}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}